/**
 * Generate the page-style navigation
 * on mobile.
 */
import LocationUtils from "../util/location-utils";

class NavMobile {

    constructor() {
        this.container = document.querySelector('.nav-mobile');
        this.menu = this.container.querySelector('.menu-container');
        this.linkList = this.menu.querySelector('ul.link-list');
        this.backButton = this.container.querySelector('.back-button');
        this.toggleButton = this.container.querySelector('#nav-check');
        this.logo = this.container.querySelector('.homelink');
        this.userZipInput = this.container.querySelector('#user-zip');
        this.zipForm = this.container.querySelector('.search-section .zip-entry');
        this.zipEntryContainer = this.container.querySelector('.zip-entry');
        this.searchSection = this.container.querySelector('.search-section');
        this.pages = Array.from(this.container.querySelectorAll('.page:not(.nested)'));

        this.isSticky = this.container.dataset.isSticky === "true";
        this.hasAnnouncement = this.container.dataset.hasAnnouncement === "true";
        this.pageMap = new Map();
        this.currentPage = 0;

        this.initPages();

        this.clearButton = this.pageMap.get("root").querySelector('.search-wrapper .close-button');
        this.searchInput = this.pageMap.get("root").querySelector('.search-wrapper input');

        this.registerListeners();

        this.userZipInput.addEventListener('focus', () => {
            this.userZipInput.setSelectionRange(0, this.userZipInput.value.length);
        });
        this.userZipInput.addEventListener('input', () => {
            this.sizeZipInputToValue();
        });

        // (SW): The search event listener is not supported in Firefox
        // this.userZipInput.addEventListener('search', e => {
        //     e.preventDefault();
        //     let value = e.target.value;
        //     LocationUtils.writeZipCookie(value);
        //     location.reload();
        // });

        this.zipForm.addEventListener('submit', e => {
            e.preventDefault();
            let value = this.userZipInput.value;
            LocationUtils.writeZipCookie(value);
            location.reload();
        });

        this.sizeZipInputToValue();
    }

    sizeZipInputToValue() {
        let value = this.userZipInput.value;
        if(value.length === 0) {
            value = this.userZipInput.getAttribute('placeholder');
        }

        const span = document.createElement('span');
        span.classList.add('cta');
        span.style.visibility = 'hidden';
        span.innerText = value;
        document.body.append(span);

        const width = span.getBoundingClientRect().width;
        document.body.removeChild(span);

        this.userZipInput.style.width = `${width + 5}px`;
    }

    static get ANNOUNCEMENT_HEIGHT() {
        return 42;
    }

    /**
     * Lays out the page for one "level" of the
     * menu.
     * Extend the width of the container to be
     * maxDepth times the full width of the screen,
     * then place the left position of each page
     * according to their depth in the list and add
     * to a hash map for retrieval.
     */
    initPages() {
        const _this = this;

        if(!this.hasAnnouncement) {
            this.menu.style.paddingTop = 0;
        }

        let maxDepth = 0;
        this.pages.forEach((page) => {
            const level = NavMobile.getLevel(page);
            const depth = Number.parseInt(page.dataset.depth);
            maxDepth = depth > maxDepth ? depth : maxDepth;

            page.querySelectorAll('li a').forEach((link) => {
                link.addEventListener('click', (e) => {
                    e.preventDefault();
                    _this.handleLinkClicked(e, link, page, _this);
                    return false;
                }, link.href !== "#");
            });

            page.style.left = depth === 0 ? '0vw' : '100vw';
            _this.pageMap.set(level, page);
        });

        // this.menu.style.width = `${(maxDepth + 1) * 100}vw`;
        this.togglePageVisibility(this.pageMap.get("root"));
    }

    static getLevel(page) {
        if(page) {
            const parent = page.closest(`.page[data-depth="${Number.parseInt(page.dataset.depth) - 1}"]`);
            if(parent) {
                return `${page.dataset.level}-${parent.dataset.level}`;
            } else {
                return page.dataset.level;
            }
        }

        return '';
    }

    handleResize(instance) {
        instance.adjustWidth();

        if(window.innerHeight < window.innerWidth) {
            this.pageMap.get("root").style.overflowY = 'scroll';
        }
    }

    adjustWidth() {
        this.container.style.width = `${document.querySelector('header.header').getBoundingClientRect().width}px`;
    }

    registerListeners() {
        this.toggleButton.addEventListener('change', e => this.handleNavToggled(e, this));
        window.addEventListener('resize', () => this.handleResize(this));
        document.addEventListener('scroll', e => this.shouldNavStick(e, this));
        this.backButton.addEventListener('click', () => this.handleBackClicked(this));
        this.searchInput.addEventListener('input', e => this.handleSearchInput(e, this));

        if(this.clearButton) {
            this.clearButton.addEventListener('click', () => this.handleClearButtonClicked(this));
        }
    }

    handleNavToggled(e, instance) {
        if(!e.target.checked) {
            if(!instance.isSticky) {
                instance.menu.style.top = 0;
            }

            document.body.style.overflowY = 'auto';
            instance.togglePageVisibility(this.pageMap.get('root'));
            instance.goToPage(0);
            instance.searchSection.classList.remove('visible');
        } else {
            if(!instance.isSticky) {
                instance.menu.style.top = `${window.scrollY}px`;
            }

            document.body.style.overflowY = 'hidden';

            instance.searchSection.classList.add('visible');
        }
    }

    /**
     * Stick to top if scrolled past the
     * sitewide announcement
     * @param e
     * @param instance
     */
    shouldNavStick(e, instance) {
        if(instance.isSticky) {
            if(NavMobile.ANNOUNCEMENT_HEIGHT - window.scrollY <= 0) {
                instance.adjustWidth();
                instance.container.classList.add('sticky');
            } else {
                instance.container.classList.remove('sticky');
            }
        }
    }

    togglePageVisibility(page) {
        if(!page) return;

        if(NavMobile.getLevel(page) === "root") {
            page.style.overflowX = 'hidden';
            page.style.overflowY = 'hidden';
        }

        this.pages.forEach(p => {
            if(NavMobile.getLevel(page) === NavMobile.getLevel(p)) {
                p.style.opacity = 1;
                p.style.pointerEvents = 'auto';
            } else if(p.dataset.depth === page.dataset.depth) {
                p.style.opacity = 0;
                p.style.pointerEvents = 'none';
            }
        });

        if(page.getBoundingClientRect().height > window.innerHeight || window.innerHeight < window.innerWidth) {
            this.pageMap.get("root").style.overflowY = 'scroll';
        }
    }

    toggleBackButtonVisibility(isVisible) {
        this.backButton.style.display = isVisible ? 'block' : 'none';
        this.logo.style.display = isVisible ? 'none' : 'block';
    }

    getNextPage(nextLevel, nextDepth) {
        for(let i = 0; i < this.pages.length; i++) {
            const p = this.pages[i];
            if(NavMobile.getLevel(p) === nextLevel && Number.parseInt(p.dataset.depth) === nextDepth) {
                return p;
            }
        }

        return null;
    }

    goToPage(depth) {
        this.linkList.style.transform = `translateX(${depth * -100}vw`;
        this.toggleBackButtonVisibility(depth > 0);
        this.currentPage = depth;

        if(depth > 0) {
            this.searchSection.classList.remove('visible');
        } else {
            this.searchSection.classList.add('visible');
        }
    }

    /**
     * Toggle visibility of selected page and scroll
     * menu one screen width to the left or execute
     * link if valid.
     * @param e
     * @param link
     * @param page
     * @param instance
     * @returns {undefined}
     */
    handleLinkClicked(e, link, page, instance) {
        e.preventDefault();
        const isParent = Boolean(Number(link.dataset.isParent));
        if(!isParent) {
            window.location.href = link.href;
            return;
        }

        const nextPage = instance.getNextPage(`${link.dataset.next}-${page.dataset.level}`, Number.parseInt(page.dataset.depth) + 1);
        if(nextPage) {
            instance.togglePageVisibility(nextPage);
            instance.goToPage(nextPage.dataset.depth);
        }

        return false;
    }

    handleBackClicked(instance) {
        instance.goToPage(instance.currentPage - 1);
        this.pageMap.get('root').scrollTo({
            top: 0,
            behavior: 'smooth'
        });
        this.pageMap.get('root').style.overflowY = 'hidden';
    }

    toggleClearButton(isVisible) {
        if(!this.clearButton) return;

        this.clearButton.style.visibility = isVisible ? 'visible' : 'hidden';
    }

    handleClearButtonClicked(instance) {
        instance.searchInput.value = "";
        instance.toggleClearButton(false);
    }

    handleSearchInput(e, instance) {
        instance.toggleClearButton(e.target.value.length > 0);
    }
}

document.addEventListener('DOMContentLoaded',  () => {
    window.navMobile = new NavMobile();
});